import {
  type ViewExtensionContext,
  ViewExtensionProvider,
} from '@blocksuite/affine-ext-loader';
import { ParagraphBlockModel } from '@blocksuite/affine-model';
import { BlockViewExtension, FlavourExtension } from '@blocksuite/std';
import { literal } from 'lit/static-html.js';
import { z } from 'zod';

import { effects } from './effects';
import { ParagraphMarkdownExtension } from './markdown.js';
import { ParagraphBlockConfigExtension } from './paragraph-block-config.js';
import {
  ParagraphKeymapExtension,
  ParagraphTextKeymapExtension,
} from './paragraph-keymap.js';

const placeholderI18nKeys: Record<string, string> = {
  text: 'com.affine.blocksuite.paragraph.placeholder',
  h1: 'com.affine.blocksuite.paragraph.heading.h1',
  h2: 'com.affine.blocksuite.paragraph.heading.h2',
  h3: 'com.affine.blocksuite.paragraph.heading.h3',
  h4: 'com.affine.blocksuite.paragraph.heading.h4',
  h5: 'com.affine.blocksuite.paragraph.heading.h5',
  h6: 'com.affine.blocksuite.paragraph.heading.h6',
};

const placeholders = {
  text: "Type '/' for commands",
  h1: 'Heading 1',
  h2: 'Heading 2',
  h3: 'Heading 3',
  h4: 'Heading 4',
  h5: 'Heading 5',
  h6: 'Heading 6',
  quote: '',
};

const optionsSchema = z.object({
  getPlaceholder: z.optional(
    z.function().args(z.instanceof(ParagraphBlockModel)).returns(z.string())
  ),
  getPlaceholderI18nKey: z.optional(
    z
      .function()
      .args(z.instanceof(ParagraphBlockModel))
      .returns(z.string().optional())
  ),
});

export class ParagraphViewExtension extends ViewExtensionProvider<
  z.infer<typeof optionsSchema>
> {
  override name = 'affine-paragraph-block';

  override schema = optionsSchema;

  override effect(): void {
    super.effect();
    effects();
  }

  override setup(
    context: ViewExtensionContext,
    options?: z.infer<typeof optionsSchema>
  ) {
    super.setup(context, options);
    const getPlaceholder =
      options?.getPlaceholder ?? (model => placeholders[model.props.type]);
    const getPlaceholderI18nKey =
      options?.getPlaceholderI18nKey ??
      (model => placeholderI18nKeys[model.props.type]);

    context.register([
      FlavourExtension('affine:paragraph'),
      BlockViewExtension('affine:paragraph', literal`affine-paragraph`),
      ParagraphTextKeymapExtension,
      ParagraphKeymapExtension,
      ParagraphBlockConfigExtension({
        getPlaceholder,
        getPlaceholderI18nKey,
      }),
      ParagraphMarkdownExtension,
    ]);
  }
}
